import { createPortal } from 'react-dom';
import { formatPrice } from '../../../../modules/currency';
import { GlassCard } from '../../shared/GlassCard';
import { QuantityStepper } from './QuantityStepper';
import { RipButton } from './RipButton';

export interface BottomActionProps {
  quantity: number;
  onQuantityChange: (next: number) => void;
  onBuy: () => void;
  /** Per-unit price (number like 25 or string like "25.0"). */
  unitPrice: number | string;
  /** Minimum / maximum quantity the user may pick. */
  minQty?: number;
  maxQty?: number;
  /** Pack not purchasable (e.g. "Restocking") — the RIP pill reads this
   *  label, locked, and the stepper locks with it. */
  unavailableLabel?: string;
}

interface BarProps {
  quantity: number;
  minQty: number;
  maxQty: number;
  onQuantityChange: (next: number) => void;
  onBuy: () => void;
  totalLabel: string;
  /** Nothing to buy — the pill reads this instead of the RIP call. */
  lockedLabel: string | null;
}

/** Room the phone's dock takes from the bottom of the page: 8 above, the
 *  46 row, 8 below; the host inset is already `#root`'s padding. Consumed
 *  by PackLayout as an in-flow spacer. */
export const MOBILE_RAIL_SPACER_HEIGHT = '62px';

function ariaFor(quantity: number, totalLabel: string): string {
  return `Open ${quantity} pack${quantity === 1 ? '' : 's'} for ${totalLabel}`;
}

/**
 * Phone dock — the stepper capsule and "RIP FOR $X" in one row, 8 above
 * the bottom edge (repackz-handoff §8.2). Portalled to <body> so no
 * ancestor transform hijacks `fixed`. The wrapper is its own `@container`
 * spanning the viewport minus a classic scrollbar — the width the pack
 * grid's `@[800px]` query measures — so it hides exactly when the desktop
 * card appears. z-[45] clears Backdrop's inset cover (z-40), under dialogs.
 */
function MobileBar({ quantity, minQty, maxQty, onQuantityChange, onBuy, totalLabel, lockedLabel }: BarProps) {
  return createPortal(
    <div className="@container fixed inset-x-0 bottom-0 z-[45]">
      <div
        data-co-component="BottomActionShell"
        className="mx-auto flex max-w-[480px] items-center gap-2 px-4 pt-2 @[800px]:hidden"
        style={{ paddingBottom: 'calc(8px + var(--page-safe-bottom, 0px))' }}
      >
        <QuantityStepper
          value={quantity}
          min={minQty}
          max={maxQty}
          onChange={onQuantityChange}
          variant="dock"
          disabled={lockedLabel !== null}
        />
        <RipButton
          onClick={onBuy}
          variant="dock"
          label={lockedLabel ?? `Rip for ${totalLabel}`}
          ariaLabel={lockedLabel ?? ariaFor(quantity, totalLabel)}
          disabled={lockedLabel !== null}
        />
      </div>
    </div>,
    document.body,
  );
}

/** Desktop — the second glass card of the sidebar (Figma 19074:254279):
 *  the total and the stepper over a full-width RIP. */
function DesktopBar({ quantity, minQty, maxQty, onQuantityChange, onBuy, totalLabel, lockedLabel }: BarProps) {
  const ripLabel = quantity === 1 ? 'Rip pack' : `Rip ${quantity} packs`;
  return (
    <div data-co-component="BottomActionShell" className="hidden @[800px]:block">
      <GlassCard className="flex flex-col gap-4">
        <div className="flex items-center justify-between gap-3">
          <span className="truncate text-[36px] font-semibold leading-9 tracking-[-1.08px]">{totalLabel}</span>
          <QuantityStepper
            value={quantity}
            min={minQty}
            max={maxQty}
            onChange={onQuantityChange}
            disabled={lockedLabel !== null}
          />
        </div>
        <RipButton
          onClick={onBuy}
          label={lockedLabel ?? ripLabel}
          ariaLabel={lockedLabel ?? ariaFor(quantity, totalLabel)}
          disabled={lockedLabel !== null}
        />
      </GlassCard>
    </div>
  );
}

/**
 * Pack-page buy controls of the glass template.
 *
 *   < 800px — fixed dock at the bottom of the screen.
 *   ≥ 800px — glass price card inside the sidebar.
 */
export function BottomAction({
  quantity,
  onQuantityChange,
  onBuy,
  unitPrice,
  minQty = 1,
  maxQty = 99,
  unavailableLabel,
}: BottomActionProps) {
  const totalAmount = (Number(unitPrice) || 0) * Math.max(quantity, 1);
  const lockedLabel = unavailableLabel ?? (maxQty <= 0 ? 'Sold out' : null);

  const shared: BarProps = {
    quantity,
    minQty,
    maxQty,
    onQuantityChange,
    onBuy,
    totalLabel: formatPrice(totalAmount, 2),
    lockedLabel,
  };

  return (
    <>
      <MobileBar {...shared} />
      <DesktopBar {...shared} />
    </>
  );
}
